import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"
import { OrbitControls } from "three/addons/controls/OrbitControls.js"
import { Line2 } from "three/addons/lines/Line2.js"
import { LineMaterial } from "three/addons/lines/LineMaterial.js"
import { LineGeometry } from "three/addons/lines/LineGeometry.js"
import { SimulationEngine } from "celest/core/simulation/simulation_engine"
import { BodyRegistry } from "celest/core/bodies/body_registry"
import { CelestialBodyData, BodyType } from "celest/core/bodies/celestial_body_data"
import { cruxDefinitionFromJson } from "celest/core/crux/crux_definition"
import { ConditionEvaluator } from "celest/core/crux/condition_evaluator"
import { generateOrbitPath } from "celest/core/bodies/orbit_path_generator"
import { SpacecraftState } from "celest/core/spacecraft/spacecraft_state"
import { ChemicalEngine } from "celest/core/spacecraft/propulsion_model"
import { Vector3d } from "celest/core/math/vector3d"
import { AU_KM, G0, GM, RADIUS, SECONDS_PER_DAY, TWO_PI } from "celest/core/math/constants"
import { stateToElements, elementsToState, positionAtTrueAnomaly } from "celest/core/math/orbital_math"
import { periapsis, apoapsis } from "celest/core/math/orbital_elements"

import type { Maneuver } from "celest/core/simulation/maneuver"
import type { StateVector } from "celest/core/math/state_vector"

const WORLD_UNITS_PER_AU = 10

function kmToWorld(km: number): number {
  return (km / AU_KM) * WORLD_UNITS_PER_AU
}

function vec3dToThree(v: Vector3d): THREE.Vector3 {
  // Ecliptic XYZ → Three.js (Y-up): X→X, Z→Y, Y→Z
  return new THREE.Vector3(kmToWorld(v.x), kmToWorld(v.z), kmToWorld(v.y))
}

let lineResolution = new THREE.Vector2(1280, 720)

function makePositions(points: THREE.Vector3[]): number[] {
  const positions: number[] = []
  for (const p of points) positions.push(p.x, p.y, p.z)
  return positions
}

/** Glow layer definition — multipliers relative to base radius */
const GLOW_LAYERS = [
  { radiusMul: 4,   opacityMul: 0.03 },
  { radiusMul: 2,   opacityMul: 0.07 },
  { radiusMul: 1,   opacityMul: 0.15 },
]

/** Max screen-space pixels the outermost glow layer should ever occupy */
const MAX_GLOW_PIXELS = 18

interface OrbitGlowEntry {
  points: THREE.Vector3[]
  curve: THREE.CatmullRomCurve3
  color: number
  coreWidth: number
  baseRadius: number
  opacity: number
  group: THREE.Group
  /** Currently built tube radius (so we know when to rebuild) */
  builtRadius: number
}

/** Build (or rebuild) tube glow meshes inside an existing group */
function buildTubeGlows(entry: OrbitGlowEntry, effectiveRadius: number) {
  // Remove old tube meshes (keep the Line2 core which is always last child)
  while (entry.group.children.length > 1) {
    const child = entry.group.children[0]
    entry.group.remove(child)
    if (child instanceof THREE.Mesh) child.geometry.dispose()
  }

  const segments = Math.max(entry.points.length * 2, 64)
  for (const layer of GLOW_LAYERS) {
    const geo = new THREE.TubeGeometry(entry.curve, segments, effectiveRadius * layer.radiusMul, 32, true)
    const mat = new THREE.MeshBasicMaterial({
      color: entry.color,
      transparent: true,
      opacity: entry.opacity * layer.opacityMul,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    })
    // Insert before the Line2 core (last child)
    const mesh = new THREE.Mesh(geo, mat)
    entry.group.children.splice(entry.group.children.length - 1, 0, mesh)
    mesh.parent = entry.group
  }
  entry.builtRadius = effectiveRadius
}

/** Create a glowing orbit line. Returns the group and an OrbitGlowEntry for dynamic updates. */
function makeGlowLine(points: THREE.Vector3[], color: number, coreWidth: number = 2.5, glowRadius: number = 0.03, opacity: number = 1): { group: THREE.Group, entry: OrbitGlowEntry } {
  const group = new THREE.Group()
  const curve = new THREE.CatmullRomCurve3(points, true)

  // Core line first (will be last child after tube inserts)
  const coreGeo = new LineGeometry()
  coreGeo.setPositions(makePositions(points))
  const coreMat = new LineMaterial({
    color,
    linewidth: coreWidth,
    transparent: true,
    opacity: opacity * 0.85,
    worldUnits: false,
    resolution: lineResolution,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  })
  group.add(new Line2(coreGeo, coreMat))

  const entry: OrbitGlowEntry = { points, curve, color, coreWidth, baseRadius: glowRadius, opacity, group, builtRadius: 0 }
  buildTubeGlows(entry, glowRadius)

  return { group, entry }
}

// Semi-major axes in km — used to compute inter-orbit gaps for body sizing
const ORBIT_RADII_KM: Record<string, number> = {
  Sun: 0,
  Mercury: 57_909_050,
  Venus: 108_208_000,
  Earth: 149_598_023,
  Mars: 227_939_200,
  Jupiter: 778_570_000,
  Saturn: 1_433_530_000,
  Uranus: 2_872_460_000,
  Neptune: 4_495_060_000,
  Moon: 384_400,  // around Earth, not Sun
}

// Planets in order from Sun, for gap computation
const PLANETS_ORDERED = ["Mercury", "Venus", "Earth", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune"]

/**
 * Get the orbit radius (in world units) of the next inner neighbor.
 * Returns 0 for Mercury (innermost) or bodies not in the planet list.
 */
function innerOrbitWorld(name: string): number {
  const idx = PLANETS_ORDERED.indexOf(name)
  if (idx <= 0) return 0
  return kmToWorld(ORBIT_RADII_KM[PLANETS_ORDERED[idx - 1]] || 0)
}

/** Own orbit radius in world units */
function ownOrbitWorld(name: string): number {
  return kmToWorld(ORBIT_RADII_KM[name] || 0)
}

// Design system gold — oklch(0.78 0.16 75) ≈ #d4a030
const SPACECRAFT_COLOR = 0xd4a030
// Cyan for planned trajectory preview — design system instrument color
const PREVIEW_COLOR = 0x44ccdd

const BODY_COLORS: Record<string, number> = {
  Sun: 0xffdd44, Mercury: 0xaaaaaa, Venus: 0xeedd88, Earth: 0x4488ff,
  Moon: 0xcccccc, Mars: 0xdd6644, Jupiter: 0xddaa77, Saturn: 0xeecc88,
  Uranus: 0x88ccdd, Neptune: 0x4466dd,
}

const BODY_TEXTURES: Record<string, string> = {
  Sun: "/textures/planets/sunmap.jpg",
  Mercury: "/textures/planets/mercurymap.jpg",
  Venus: "/textures/planets/venusmap.jpg",
  Earth: "/textures/planets/earthmap1k.jpg",
  Moon: "/textures/planets/moonmap1k.jpg",
  Mars: "/textures/planets/mars_1k_color.jpg",
  Jupiter: "/textures/planets/jupitermap.jpg",
  Saturn: "/textures/planets/saturnmap.jpg",
  Uranus: "/textures/planets/uranusmap.jpg",
  Neptune: "/textures/planets/neptunemap.jpg",
  Ariel: "/textures/moons/cropped/ariel.png",
  Callisto: "/textures/moons/cropped/callisto.png",
  Deimos: "/textures/moons/cropped/deimos.png",
  Despina: "/textures/moons/cropped/despina.png",
  Dione: "/textures/moons/cropped/dione.png",
  Enceladus: "/textures/moons/cropped/enceladus.png",
  Epimetheus: "/textures/moons/cropped/epimetheus.png",
  Europa: "/textures/moons/cropped/europa.png",
  Ganymede: "/textures/moons/cropped/ganymede.png",
  Helene: "/textures/moons/cropped/helene.png",
  Iapetus: "/textures/moons/cropped/iapetus.png",
  Io: "/textures/moons/cropped/io.png",
  Janus: "/textures/moons/cropped/janus.png",
  Larissa: "/textures/moons/cropped/larissa.png",
  Mimas: "/textures/moons/cropped/mimas.png",
  Miranda: "/textures/moons/cropped/miranda.png",
  Oberon: "/textures/moons/cropped/oberon.png",
  Pandora: "/textures/moons/cropped/pandora.png",
  Phobos: "/textures/moons/cropped/phobos.png",
  Phoebe: "/textures/moons/cropped/phoebe.png",
  Proteus: "/textures/moons/cropped/proteus.png",
  Puck: "/textures/moons/cropped/puck.png",
  Rhea: "/textures/moons/cropped/rhea.png",
  Telesto: "/textures/moons/cropped/telesto.png",
  Tethys: "/textures/moons/cropped/tethys.png",
  Titan: "/textures/moons/cropped/titan.png",
  Titania: "/textures/moons/cropped/titania.png",
  Triton: "/textures/moons/cropped/triton.png",
  Umbriel: "/textures/moons/cropped/umbriel.png",
}

const textureLoader = new THREE.TextureLoader()

export default class SimulationController extends Controller {
  static values = { cruxSlug: String, shipSlug: String }
  static targets = ["canvas", "hud", "clock", "fuelBar", "deltaV", "timeControls", "burnPanel"]

  declare cruxSlugValue: string
  declare shipSlugValue: string
  declare canvasTarget: HTMLElement
  declare clockTarget: HTMLElement
  declare fuelBarTarget: HTMLElement
  declare deltaVTarget: HTMLElement
  declare burnPanelTarget: HTMLElement
  declare hasBurnPanelTarget: boolean

  private scene!: THREE.Scene
  private camera!: THREE.PerspectiveCamera
  private renderer!: THREE.WebGLRenderer
  private controls!: OrbitControls
  private engine!: SimulationEngine
  private registry!: BodyRegistry
  private bodyMeshes = new Map<string, THREE.Mesh>()
  /** Inner neighbor orbit radius per body (world units) for screen-space gap calc */
  private bodyInnerOrbit = new Map<string, number>()
  /** True radius per body (world units) */
  private bodyTrueRadius = new Map<string, number>()
  private spacecraftMesh!: THREE.Mesh
  private trailPoints: THREE.Vector3[] = []
  private trailLine!: THREE.Line
  private animationId = 0
  private lastTime = 0
  private raycaster = new THREE.Raycaster()
  private mouse = new THREE.Vector2()
  private focusedMesh: THREE.Mesh | null = null
  private targetZoomDist: number | null = null
  private orbitGlows: OrbitGlowEntry[] = []
  /** Satellite orbit line groups, keyed by satellite name, in parent-relative coords */
  private satelliteOrbitGroups = new Map<string, THREE.Group>()

  // --- Burn planner state ---
  private burnPrograde = 0   // m/s
  private burnNormal = 0     // m/s
  private burnRadial = 0     // m/s
  /** Time offset from current sim time for burn point (seconds) */
  private burnTimeOffset = 0
  /** Max burn time offset — 5 years in seconds */
  private static readonly MAX_BURN_TIME_OFFSET = 5 * 365.25 * 86400
  /** The preview orbit line group */
  private previewGroup: THREE.Group | null = null
  private previewGlowEntry: OrbitGlowEntry | null = null
  /** Burn marker mesh shown on current orbit at the burn point */
  private burnMarkerMesh: THREE.Mesh | null = null
  /** Arrival marker — shows where on the preview orbit the spacecraft will be at periapsis */
  private arrivalMarkerMesh: THREE.Mesh | null = null
  /** SOI boundary spheres keyed by body name */
  private soiSpheres = new Map<string, THREE.Mesh>()
  /** The spacecraft's current orbit group (so we can update it after maneuvers) */
  private spacecraftOrbitGroup: THREE.Group | null = null
  private spacecraftOrbitEntry: OrbitGlowEntry | null = null
  private conditionEvaluator: ConditionEvaluator | null = null

  async connect() {
    try {
      const fetches: Promise<Response>[] = [
        fetch(`/api/cruxes/${this.cruxSlugValue}.json`),
        fetch("/api/bodies.json"),
      ]
      if (this.shipSlugValue) {
        fetches.push(fetch(`/api/spacecraft/${this.shipSlugValue}.json`))
      }

      const responses = await Promise.all(fetches)

      const cruxJson = await responses[0].json()
      const bodiesJson = await responses[1].json()
      const shipJson = responses[2] ? await responses[2].json() : null

      const crux = cruxDefinitionFromJson(cruxJson.definition_json || cruxJson)
      this.registry = BodyRegistry.fromApiResponse(bodiesJson)

      // Override spacecraft stats with selected ship template
      if (shipJson) {
        crux.spacecraftName = shipJson.name
        crux.spacecraftDryMass = shipJson.dry_mass_kg
        crux.spacecraftFuelMass = shipJson.fuel_capacity_kg
        crux.spacecraftISP = shipJson.isp_vacuum_s
        crux.spacecraftMaxThrust = shipJson.thrust_vacuum_n
      }

      this.initThreeJS()
      this.initSimulation(crux)
      this.buildScene()
      this.initBurnPanel()
      this.animate(performance.now())
    } catch (err) {
      console.error("Simulation init failed:", err)
    }
  }

  disconnect() {
    if (this.animationId) cancelAnimationFrame(this.animationId)
    this.renderer?.dispose()
  }

  // --- Three.js Setup ---

  private initThreeJS() {
    const container = this.canvasTarget
    const w = container.clientWidth
    const h = container.clientHeight

    this.scene = new THREE.Scene()

    // Skybox from astrophotography
    const skyboxTexture = textureLoader.load("/textures/skybox/Stellarium3.jpg")
    skyboxTexture.mapping = THREE.EquirectangularReflectionMapping
    this.scene.background = skyboxTexture
    this.scene.backgroundIntensity = 0.1

    this.camera = new THREE.PerspectiveCamera(60, w / h, 0.001, 1000)
    this.camera.position.set(0, 15, 15)

    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setSize(w, h)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    container.appendChild(this.renderer.domElement)

    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.minDistance = 0.01
    this.controls.maxDistance = 100000
    this.controls.enableZoom = false // replaced by custom proportional zoom

    this.renderer.domElement.addEventListener('wheel', (e) => this.onWheel(e), { passive: false })

    this.renderer.domElement.addEventListener('pointerdown', (e) => this.onPointerDown(e))
    this.renderer.domElement.addEventListener('pointerup', (e) => this.onPointerUp(e))
    this.renderer.domElement.style.cursor = 'default'

    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2))
    const sunLight = new THREE.PointLight(0xffffee, 1.5, 0, 0.1)
    this.scene.add(sunLight)

    lineResolution.set(w, h)

    const resizeObserver = new ResizeObserver(() => {
      const nw = container.clientWidth
      const nh = container.clientHeight
      this.camera.aspect = nw / nh
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(nw, nh)
      lineResolution.set(nw, nh)
    })
    resizeObserver.observe(container)
  }

  // --- Simulation Setup ---

  private initSimulation(crux: ReturnType<typeof cruxDefinitionFromJson>) {
    const spacecraft = new SpacecraftState({
      id: "player",
      name: crux.spacecraftName,
      state: {
        position: crux.startPosition,
        velocity: crux.startVelocity,
        time: crux.startTimeJD,
        centralBody: crux.startSOIBody,
      },
      dryMass: crux.spacecraftDryMass,
      fuelMass: crux.spacecraftFuelMass,
      fuelCapacity: crux.spacecraftFuelMass,
      engine: new ChemicalEngine(
        "Main Engine",
        crux.spacecraftMaxThrust,
        0,
        crux.spacecraftISP,
        0,
      ),
    })

    const soiBody = crux.startSOIBody
    const startTimeJD = crux.startTimeJD

    this.engine = new SimulationEngine(this.registry, spacecraft, soiBody, startTimeJD)

    // Wire up conditions
    const allConditions = [...crux.victoryConditions, ...crux.failureConditions]
    if (allConditions.length > 0) {
      const evaluator = new ConditionEvaluator()
      for (const c of allConditions) evaluator.addCondition(c)
      this.engine.setConditionEvaluator(evaluator)
      this.conditionEvaluator = evaluator
    }

    // Subscribe to events
    this.engine.events.onTimeChanged.on(() => this.updateHUD())
    this.engine.events.onSpacecraftMoved.on(() => this.updateSpacecraft())
    this.engine.events.onVictoryConditionMet.on((id) => {
      this.engine.pause()
      this.showMessage(`Mission Complete`, "success")
    })
    this.engine.events.onFailureConditionMet.on((id) => {
      this.engine.pause()
      const msg = id.startsWith("time_limit") ? "Mission time limit exceeded"
        : id.startsWith("fuel_exhausted") ? "Fuel exhausted"
        : id.startsWith("crash") || id.startsWith("impact") ? "Crashed into body"
        : id
      this.showMessage(msg, "failure")
    })
    this.engine.events.onOrbitChanged.on(() => this.rebuildSpacecraftOrbit())
    this.engine.events.onSOIChanged.on((newBody) => {
      this.resetBurn()
      this.trailPoints = []
      this.trailLine.geometry.dispose()
      this.trailLine.geometry = new THREE.BufferGeometry()
      console.log(`SOI transition: now orbiting ${newBody}`)
    })
  }

  // --- Scene Building ---

  private buildScene() {
    const positions = this.registry.computeAllPositions(this.engine.time.currentTimeJD)
    const soiBodyName = this.engine.state.currentSOIBody
    const soiBody = this.registry.getByName(soiBodyName)
    const soiPos = positions.get(soiBodyName) || Vector3d.Zero
    const soiThree = vec3dToThree(soiPos)

    // Determine view scale from spacecraft altitude
    const scAltitudeKm = this.engine.state.spacecraft.state.position.magnitude
    const viewScaleKm = scAltitudeKm * 3 // show ~3x the orbit radius
    const viewScaleWorld = kmToWorld(viewScaleKm)

    // Add the SOI body (the body we're orbiting)
    this.addBody(soiBodyName, positions, viewScaleWorld)

    // Add the SOI body's parent (e.g., Sun for Earth missions, Earth for Moon missions)
    if (soiBody?.parentBodyName) {
      this.addBody(soiBody.parentBodyName, positions, viewScaleWorld)
    }

    // Add nearby siblings/children visible at this scale
    const keyBodies = new Set([
      "Sun", "Mercury", "Venus", "Earth", "Moon", "Mars",
      "Jupiter", "Saturn", "Uranus", "Neptune",
    ])
    for (const name of keyBodies) {
      if (!this.bodyMeshes.has(name)) {
        this.addBody(name, positions, viewScaleWorld)
      }
    }

    // Add all moons from the registry so they appear as companions to their parent
    for (const parentName of PLANETS_ORDERED) {
      const parent = this.registry.getByName(parentName)
      if (!parent) continue
      for (const child of this.registry.getChildren(parent)) {
        if (!this.bodyMeshes.has(child.name)) {
          this.addBody(child.name, positions, viewScaleWorld)
        }
      }
    }

    // Draw the spacecraft's initial orbit around the SOI body
    this.rebuildSpacecraftOrbit()

    // Spacecraft marker — golden cone pointing along velocity
    const scGeo = new THREE.ConeGeometry(0.35, 1, 8)
    scGeo.translate(0, 0.5, 0) // base at origin, tip along +Y
    const scMat = new THREE.MeshBasicMaterial({ color: SPACECRAFT_COLOR })
    this.spacecraftMesh = new THREE.Mesh(scGeo, scMat)
    this.scene.add(this.spacecraftMesh)

    // Trail
    this.trailLine = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color: SPACECRAFT_COLOR, opacity: 0.5, transparent: true })
    )
    this.scene.add(this.trailLine)

    // Camera: frame the mission at the right scale
    const camDist = Math.max(viewScaleWorld, 0.001)

    this.controls.target.copy(soiThree)
    this.camera.position.set(
      soiThree.x,
      soiThree.y + camDist,
      soiThree.z + camDist * 0.3
    )

    this.camera.near = camDist * 0.0001
    this.camera.far = Math.max(camDist * 10000, 500)
    this.camera.updateProjectionMatrix()

    // Scale spacecraft marker to be visible at this zoom
    this.spacecraftMesh.scale.setScalar(Math.max(camDist * 0.04, 0.0001))

    // Set initial body scales
    this.updateBodyScales()
  }

  /** Add a single celestial body to the scene */
  private addBody(name: string, positions: Map<string, Vector3d>, _viewScaleWorld: number) {
    const body = this.registry.getByName(name)
    if (!body || this.bodyMeshes.has(name)) return

    try {
      const color = BODY_COLORS[name] || 0x888888

      // Create a unit sphere (radius=1) — we'll set scale dynamically
      const geo = new THREE.SphereGeometry(1, 48, 24)

      const texturePath = BODY_TEXTURES[name]
      const isStar = body.bodyType === BodyType.Star
      let mat: THREE.Material

      if (texturePath) {
        const texture = textureLoader.load(texturePath)
        texture.colorSpace = THREE.SRGBColorSpace
        if (isStar) {
          mat = new THREE.MeshBasicMaterial({ map: texture })
        } else {
          mat = new THREE.MeshStandardMaterial({
            map: texture,
            roughness: 1.0,
            metalness: 0.0,
          })
        }
      } else {
        mat = new THREE.MeshStandardMaterial({
          color,
          emissive: isStar ? color : 0x000000,
          emissiveIntensity: isStar ? 1.0 : 0,
        })
      }

      const mesh = new THREE.Mesh(geo, mat)

      const pos = positions.get(name)
      if (pos) mesh.position.copy(vec3dToThree(pos))

      // Compute sizing constraints
      const radiusKm = body.meanRadius || (RADIUS as any)[name] || 1000
      const trueRadius = kmToWorld(radiusKm)
      this.bodyTrueRadius.set(name, trueRadius)

      // Store inner orbit radius for screen-space gap sizing (planets only)
      if (name === "Sun") {
        this.bodyInnerOrbit.set(name, 0)
      } else if (body.parentBodyName === "Sun") {
        this.bodyInnerOrbit.set(name, innerOrbitWorld(name))
      }

      // Initial scale will be set by updateBodyScales()
      mesh.scale.setScalar(trueRadius)

      this.scene.add(mesh)
      this.bodyMeshes.set(name, mesh)

      // Orbit path (around parent body)
      if (body.orbitalElements && body.parentBodyName) {
        const parentGm = this.registry.getGM(body.parentBodyName)
        if (parentGm > 0 && body.orbitalElements.semiMajorAxis > 0) {
          const pathPoints = generateOrbitPath(body.orbitalElements, 128)
          const isSatellite = body.parentBodyName !== "Sun"

          if (isSatellite) {
            // Satellite orbits: store in parent-relative coordinates so we
            // can reposition + rescale the group when the parent is inflated
            const linePoints = pathPoints.map((p) => vec3dToThree(p))
            const { group: orbitGroup, entry: orbitEntry } = makeGlowLine(linePoints, color, 2, 0.03, 0.7)
            const parentPos = positions.get(body.parentBodyName) || Vector3d.Zero
            const pp = vec3dToThree(parentPos)
            orbitGroup.position.copy(pp)
            this.scene.add(orbitGroup)
            this.orbitGlows.push(orbitEntry)
            this.satelliteOrbitGroups.set(name, orbitGroup)
          } else {
            // Planet orbits: absolute coordinates
            const parentPos = positions.get(body.parentBodyName) || Vector3d.Zero
            const linePoints = pathPoints.map((p) => {
              const wp = vec3dToThree(p)
              const pp = vec3dToThree(parentPos)
              return new THREE.Vector3(wp.x + pp.x, wp.y + pp.y, wp.z + pp.z)
            })
            const { group: orbitGroup, entry: orbitEntry } = makeGlowLine(linePoints, color, 2, 0.03, 0.7)
            this.scene.add(orbitGroup)
            this.orbitGlows.push(orbitEntry)
          }
        }
      }
      // SOI boundary sphere
      if (body.soiRadius > 0 && body.bodyType !== BodyType.Star) {
        const soiRadiusWorld = kmToWorld(body.soiRadius)
        const soiGeo = new THREE.SphereGeometry(soiRadiusWorld, 48, 24)
        const soiMat = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.03,
          depthWrite: false,
          side: THREE.BackSide,
        })
        const soiMesh = new THREE.Mesh(soiGeo, soiMat)
        const pos = positions.get(name)
        if (pos) soiMesh.position.copy(vec3dToThree(pos))
        this.scene.add(soiMesh)
        this.soiSpheres.set(name, soiMesh)
      }
    } catch (err) {
      console.warn(`Celest: skipped ${name}:`, err)
    }
  }

  // --- Animation Loop ---

  private animate(time: number) {
    this.animationId = requestAnimationFrame((t) => this.animate(t))

    const deltaMs = this.lastTime ? time - this.lastTime : 16
    this.lastTime = time

    if (!this.engine.time.isPaused) {
      this.engine.tick(deltaMs / 1000)
    }

    // Update body positions — show at burn time if planning, else current sim time
    const displayTimeJD = this.burnTimeOffset > 60
      ? this.computeBurnTimeJD()
      : this.engine.time.currentTimeJD
    const positions = this.registry.computeAllPositions(displayTimeJD)
    for (const [name, mesh] of this.bodyMeshes) {
      const pos = positions.get(name)
      if (pos) mesh.position.copy(vec3dToThree(pos))
    }

    // Update SOI sphere positions
    for (const [name, mesh] of this.soiSpheres) {
      const pos = positions.get(name)
      if (pos) mesh.position.copy(vec3dToThree(pos))
    }

    // Track orbit lines to their SOI body
    const soiPos = positions.get(this.engine.state.currentSOIBody)
    if (soiPos) {
      const soiWorld = vec3dToThree(soiPos)
      if (this.spacecraftOrbitGroup) this.spacecraftOrbitGroup.position.copy(soiWorld)
      if (this.previewGroup) this.previewGroup.position.copy(soiWorld)
    }

    // Keep camera target on focused body as it moves
    if (this.focusedMesh) {
      this.controls.target.copy(this.focusedMesh.position)
    }

    // Smooth zoom animation toward focus target
    if (this.focusedMesh && this.targetZoomDist !== null) {
      const currentDist = this.camera.position.distanceTo(this.controls.target)
      if (currentDist > 0.000001) {
        // Exponential interpolation in log-space for smooth scale traversal
        const alpha = 1 - Math.pow(0.01, deltaMs / 1000)
        const newDist = currentDist * Math.pow(this.targetZoomDist / currentDist, alpha)
        const dir = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize()
        this.camera.position.copy(this.controls.target).addScaledVector(dir, newDist)

        // Stop when close enough (within 2% in log-space)
        if (Math.abs(Math.log(currentDist / this.targetZoomDist)) < 0.02) {
          this.targetZoomDist = null
        }
      }
    }

    // Dynamic near/far planes so clipping adapts to zoom level
    const camDist = this.camera.position.distanceTo(this.controls.target)
    this.camera.near = Math.max(camDist * 0.0001, 0.000001)
    this.camera.far = Math.max(camDist * 10000, 500)
    this.camera.updateProjectionMatrix()

    this.updateBodyScales()
    this.updateOrbitGlows()
    this.controls.update()
    this.renderer.render(this.scene, this.camera)
  }

  // Planet groups that maintain proportional sizing within each group
  private static INNER_PLANETS = ["Mercury", "Venus", "Earth", "Mars"]
  private static OUTER_PLANETS = ["Jupiter", "Saturn", "Uranus", "Neptune"]

  /**
   * Dynamically scale body meshes based on screen-space orbit gaps,
   * maintaining natural size proportions within inner and outer planet groups.
   *
   * For each planet, draw a line from the Sun through the planet and find
   * where it crosses the next inner orbit. The planet's visual radius is
   * set to a fraction of the screen-space distance between the planet and
   * that inner orbit point.
   *
   * Within each group (inner planets, outer planets), all planets share the
   * same scale multiplier — determined by the most space-constrained planet
   * in the group. This preserves natural size ratios (e.g. Earth > Mars > Venus > Mercury).
   */
  private updateBodyScales() {
    const MIN_PIXELS = 8
    const GAP_FRACTION = 0.33
    // Extra gap space beyond the planet for satellites (moons).
    // The satellite system is compressed to fit in this zone.
    const SAT_ZONE = 0.12
    const canvasHeight = this.canvasTarget.clientHeight
    const canvasWidth = this.canvasTarget.clientWidth
    const fovFactor = 1 / Math.tan((this.camera.fov * Math.PI / 180) / 2)

    // Helper: project world position to screen pixels
    const _v = new THREE.Vector3()
    const toScreen = (worldPos: THREE.Vector3): THREE.Vector2 => {
      _v.copy(worldPos).project(this.camera)
      return new THREE.Vector2(
        (_v.x * 0.5 + 0.5) * canvasWidth,
        (-_v.y * 0.5 + 0.5) * canvasHeight,
      )
    }

    const pixelsToWorld = (pixels: number, dist: number) =>
      pixels * dist / (fovFactor * canvasHeight * 0.5)

    // Sun position (needed for direction calculation)
    const sunMesh = this.bodyMeshes.get("Sun")
    const sunPos = sunMesh ? sunMesh.position : new THREE.Vector3()

    // --- Pass 1: compute unconstrained desired radius for each planet ---
    // For each planet, find the max radius allowed by its screen-space gap,
    // then express it as a multiplier over true radius.
    const gapMultipliers = new Map<string, number>()

    for (const name of [...SimulationController.INNER_PLANETS, ...SimulationController.OUTER_PLANETS]) {
      const mesh = this.bodyMeshes.get(name)
      if (!mesh) continue
      const trueRadius = this.bodyTrueRadius.get(name) || 0.001
      const dist = mesh.position.distanceTo(this.camera.position)
      if (dist < 0.00001) continue

      const innerOrbit = this.bodyInnerOrbit.get(name) || 0
      const dir = new THREE.Vector3().subVectors(mesh.position, sunPos)
      const dirLen = dir.length()
      if (dirLen < 0.00001) continue
      dir.divideScalar(dirLen)

      const innerPt = new THREE.Vector3().copy(sunPos).addScaledVector(dir, innerOrbit)
      const planetScreen = toScreen(mesh.position)
      const innerScreen = toScreen(innerPt)
      const gapPixels = planetScreen.distanceTo(innerScreen)

      const maxWorldRadius = pixelsToWorld(gapPixels * GAP_FRACTION, dist)
      // How many times larger than true radius can this planet afford to be?
      gapMultipliers.set(name, maxWorldRadius / trueRadius)
    }

    // --- Pass 2: find the most constrained multiplier in each group ---
    const groupMultiplier = (group: string[]): number => {
      let minMul = Infinity
      for (const name of group) {
        const mul = gapMultipliers.get(name)
        if (mul !== undefined && mul < minMul) minMul = mul
      }
      // Ensure at least 1× true radius
      return minMul === Infinity ? 1 : Math.max(minMul, 1)
    }

    const innerMul = groupMultiplier(SimulationController.INNER_PLANETS)
    const outerMul = groupMultiplier(SimulationController.OUTER_PLANETS)

    // --- Pass 3: apply uniform group multiplier to planets + Sun ---
    // (Satellites are handled in Pass 4)
    for (const [name, mesh] of this.bodyMeshes) {
      const body = this.registry.getByName(name)
      // Skip satellites — they're handled in Pass 4
      if (body && body.parentBodyName && body.parentBodyName !== "Sun") continue

      const trueRadius = this.bodyTrueRadius.get(name) || 0.001
      const dist = mesh.position.distanceTo(this.camera.position)
      if (dist < 0.00001) continue

      let desiredRadius: number

      if (name === "Sun") {
        const mercuryOrbit = ownOrbitWorld("Mercury")
        if (mercuryOrbit > 0) {
          const dirToCamera = new THREE.Vector3().subVectors(this.camera.position, sunPos).normalize()
          const mercuryOrbitPt = new THREE.Vector3().copy(sunPos).addScaledVector(dirToCamera, mercuryOrbit)
          const gapPixels = toScreen(sunPos).distanceTo(toScreen(mercuryOrbitPt))
          desiredRadius = pixelsToWorld(Math.max(gapPixels * GAP_FRACTION, MIN_PIXELS), dist)
        } else {
          desiredRadius = trueRadius
        }
      } else if (SimulationController.INNER_PLANETS.includes(name)) {
        desiredRadius = trueRadius * innerMul
      } else if (SimulationController.OUTER_PLANETS.includes(name)) {
        desiredRadius = trueRadius * outerMul
      } else {
        desiredRadius = trueRadius
      }

      // Min-pixels floor: ensure every body is at least visible
      const pixelsAtDesired = (desiredRadius / dist) * fovFactor * canvasHeight * 0.5
      if (pixelsAtDesired < MIN_PIXELS) {
        desiredRadius = pixelsToWorld(MIN_PIXELS, dist)
      }

      // Never shrink below true physical radius
      desiredRadius = Math.max(desiredRadius, trueRadius)

      mesh.scale.setScalar(desiredRadius)
    }

    // --- Pass 4: compress satellite systems into a zone just outside the parent ---
    // Each planet gets an extra SAT_ZONE fraction of its orbit gap beyond its
    // visual surface. The satellite system (all moons) is compressed to fit in
    // that zone, with moons proportional to each other. The direction of each
    // moon comes from its true orbital position.

    // Group satellites by parent
    const satsByParent = new Map<string, { name: string, mesh: THREE.Mesh, body: CelestialBodyData, trueOrbitDist: number }[]>()
    for (const [name, mesh] of this.bodyMeshes) {
      const body = this.registry.getByName(name)
      if (!body || !body.parentBodyName || body.parentBodyName === "Sun") continue
      const parentMesh = this.bodyMeshes.get(body.parentBodyName)
      if (!parentMesh) continue

      const trueOffset = new THREE.Vector3().subVectors(mesh.position, parentMesh.position)
      const trueOrbitDist = trueOffset.length()
      if (trueOrbitDist < 0.00001) continue

      if (!satsByParent.has(body.parentBodyName)) satsByParent.set(body.parentBodyName, [])
      satsByParent.get(body.parentBodyName)!.push({ name, mesh, body, trueOrbitDist })
    }

    for (const [parentName, sats] of satsByParent) {
      const parentMesh = this.bodyMeshes.get(parentName)!
      const parentTrueRadius = this.bodyTrueRadius.get(parentName) || 0.001
      const parentVisualRadius = parentMesh.scale.x

      // Satellite zone: from parent surface to GAP_FRACTION + SAT_ZONE of the gap
      const parentInnerOrbit = this.bodyInnerOrbit.get(parentName) || 0
      const parentOrbit = ownOrbitWorld(parentName)
      const gapWorld = parentOrbit > 0 ? parentOrbit - parentInnerOrbit : 0
      const satZoneOuter = gapWorld * (GAP_FRACTION + SAT_ZONE)
      const available = satZoneOuter - parentVisualRadius

      // Hide all satellites if there's no room
      if (available <= 0 || gapWorld <= 0) {
        for (const sat of sats) {
          sat.mesh.visible = false
          const og = this.satelliteOrbitGroups.get(sat.name)
          if (og) og.visible = false
        }
        continue
      }

      // Find the farthest satellite orbit for normalization
      const maxSatOrbit = Math.max(...sats.map(s => s.trueOrbitDist))

      // Compression scale: maps the farthest satellite to the outer edge of the zone
      // Leave 10% margin at the outer edge
      const targetOuter = parentVisualRadius + available * 0.9
      const orbitScale = targetOuter / maxSatOrbit

      // Parent's current multiplier for proportional satellite sizing
      const parentMul = parentVisualRadius / parentTrueRadius

      for (const sat of sats) {
        const satTrueRadius = this.bodyTrueRadius.get(sat.name) || 0.001

        // Compressed position from parent center
        const compressedDist = sat.trueOrbitDist * orbitScale

        // Small radius for stable visibility check (orbitScale-based)
        const satCheckRadius = satTrueRadius * orbitScale

        // Actual display size: proportional to parent as currently rendered
        let satVisualRadius = satTrueRadius * parentMul

        // Direction from true orbital position
        const trueOffset = new THREE.Vector3().subVectors(sat.mesh.position, parentMesh.position)
        const offsetDir = trueOffset.normalize()

        // Position satellite
        sat.mesh.position.copy(parentMesh.position).addScaledVector(offsetDir, compressedDist)

        // Min-pixels floor
        const satDist = sat.mesh.position.distanceTo(this.camera.position)
        if (satDist > 0.00001) {
          const pixelsAtRadius = (satVisualRadius / satDist) * fovFactor * canvasHeight * 0.5
          if (pixelsAtRadius < MIN_PIXELS) {
            satVisualRadius = pixelsToWorld(MIN_PIXELS, satDist)
          }
        }

        // Visibility check uses the small orbitScale-based radius so it
        // doesn't fluctuate with the parent's screen-space size changes
        if (compressedDist < parentVisualRadius + satCheckRadius) {
          sat.mesh.visible = false
          const og = this.satelliteOrbitGroups.get(sat.name)
          if (og) og.visible = false
        } else {
          sat.mesh.visible = true
          sat.mesh.scale.setScalar(Math.max(satVisualRadius, satTrueRadius))

          // Only show orbit lines when satellites are near true scale.
          // Scaling the group inflates tube geometry thickness, so hide
          // orbit lines when significant compression is active.
          const og = this.satelliteOrbitGroups.get(sat.name)
          if (og) {
            if (orbitScale > 0.5 && orbitScale < 2) {
              og.visible = true
              og.position.copy(parentMesh.position)
              og.scale.setScalar(orbitScale)
            } else {
              og.visible = false
            }
          }
        }
      }
    }
  }

  /** Cap orbit glow tube radius so it never exceeds MAX_GLOW_PIXELS on screen */
  private updateOrbitGlows() {
    const canvasHeight = this.canvasTarget.clientHeight
    const fovFactor = 1 / Math.tan((this.camera.fov * Math.PI / 180) / 2)
    const camDist = this.camera.position.distanceTo(this.controls.target)

    // World-space radius that would appear as MAX_GLOW_PIXELS at current zoom
    // (outermost layer is 4× base radius, so cap accounts for that)
    const maxWorldRadius = (MAX_GLOW_PIXELS * camDist) / (fovFactor * canvasHeight * 0.5) / 4

    for (const entry of this.orbitGlows) {
      const effectiveRadius = Math.min(entry.baseRadius, maxWorldRadius)
      // Rebuild when the radius has changed by >20%
      const ratio = entry.builtRadius > 0 ? effectiveRadius / entry.builtRadius : 0
      if (ratio < 0.8 || ratio > 1.2) {
        buildTubeGlows(entry, effectiveRadius)
      }
    }
  }

  // --- HUD ---

  private updateHUD() {
    const startJD = this.engine.state.startTimeJD
    const elapsed = (this.engine.time.currentTimeJD - startJD) * 86400
    const days = Math.floor(elapsed / 86400)
    const hours = Math.floor((elapsed % 86400) / 3600)
    const mins = Math.floor((elapsed % 3600) / 60)
    const secs = Math.floor(elapsed % 60)
    if (days > 0) {
      this.clockTarget.textContent = `${days}d ${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`
    } else {
      this.clockTarget.textContent = `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
    }

    const sc = this.engine.state.spacecraft
    const fuelPct = sc.fuelFraction * 100
    this.fuelBarTarget.style.width = `${Math.max(0, fuelPct)}%`
    this.deltaVTarget.textContent = sc.availableDeltaV.toFixed(1)
  }

  private updateSpacecraft() {
    const sc = this.engine.state.spacecraft
    const positions = this.registry.computeAllPositions(this.engine.time.currentTimeJD)
    const soiPos = positions.get(this.engine.state.currentSOIBody) || Vector3d.Zero

    const worldPos = vec3dToThree(sc.state.position.add(soiPos))
    this.spacecraftMesh.position.copy(worldPos)

    // Orient cone along velocity (prograde)
    const vel = sc.state.velocity.normalized
    const velWorld = vec3dToThree(vel).normalize()
    if (velWorld.lengthSq() > 0.001) {
      const target = worldPos.clone().add(velWorld)
      this.spacecraftMesh.lookAt(target)
      this.spacecraftMesh.rotateX(Math.PI / 2) // cone +Y → lookAt +Z
    }

    // Scale to camera distance
    const camDist = this.camera.position.distanceTo(this.controls.target)
    this.spacecraftMesh.scale.setScalar(Math.max(camDist * 0.04, 0.0001))

    this.trailPoints.push(worldPos.clone())
    if (this.trailPoints.length > 1000) this.trailPoints.shift()
    this.trailLine.geometry.dispose()
    this.trailLine.geometry = new THREE.BufferGeometry().setFromPoints(this.trailPoints)
  }

  private showMessage(text: string, type: "success" | "failure") {
    const div = document.createElement("div")
    div.className = "sim-message-overlay"
    const modifier = type === "success" ? "sim-message--success" : "sim-message--failure"
    const heading = type === "success" ? "Mission Complete" : "Mission Failed"
    div.innerHTML = `
      <div class="sim-message-panel ${modifier}">
        <h2 class="sim-message-heading">${heading}</h2>
        <p class="sim-message-text">${text}</p>
        <a href="/cruxes/${this.cruxSlugValue}" class="btn-secondary" data-turbo="false">Back to Briefing</a>
      </div>`
    this.canvasTarget.appendChild(div)
  }

  // --- Body Click-to-Focus ---

  private pointerDownPos = new THREE.Vector2()

  private onPointerDown(event: PointerEvent) {
    this.pointerDownPos.set(event.clientX, event.clientY)
  }

  private onPointerUp(event: PointerEvent) {
    // Ignore drags — only handle clicks (small movement threshold)
    const dx = event.clientX - this.pointerDownPos.x
    const dy = event.clientY - this.pointerDownPos.y
    if (dx * dx + dy * dy > 9) return

    const rect = this.renderer.domElement.getBoundingClientRect()
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    this.raycaster.setFromCamera(this.mouse, this.camera)

    const clickables = [...this.bodyMeshes.values(), this.spacecraftMesh]
    const intersects = this.raycaster.intersectObjects(clickables)

    if (intersects.length > 0) {
      this.focusOn(intersects[0].object as THREE.Mesh)
    }
  }

  private onWheel(event: WheelEvent) {
    event.preventDefault()
    // Cancel any focus zoom animation on manual scroll
    this.targetZoomDist = null

    const dist = this.camera.position.distanceTo(this.controls.target)
    // Each scroll tick moves 25% of current distance for faster scale traversal
    const zoomFactor = 0.25
    const direction = event.deltaY > 0 ? -1 : 1
    const delta = dist * zoomFactor * direction

    // Move camera along the camera→target vector
    const forward = new THREE.Vector3().subVectors(this.controls.target, this.camera.position).normalize()
    this.camera.position.addScaledVector(forward, delta)

    // Clamp minimum distance only
    const newDist = this.camera.position.distanceTo(this.controls.target)
    if (newDist < this.controls.minDistance) {
      this.camera.position.addScaledVector(forward, -delta)
    }
  }

  private focusOn(mesh: THREE.Mesh) {
    this.focusedMesh = mesh
    this.controls.target.copy(mesh.position)

    // Compute zoom target for bodies (not spacecraft)
    let bodyName: string | null = null
    for (const [name, m] of this.bodyMeshes) {
      if (m === mesh) { bodyName = name; break }
    }
    if (bodyName) {
      this.targetZoomDist = this.computeFocusDist(bodyName)
    }
  }

  /** Compute camera distance that frames a body's neighborhood at ~1/3 viewport width */
  private computeFocusDist(name: string): number {
    const aspect = this.canvasTarget.clientWidth / this.canvasTarget.clientHeight
    const halfFovTan = Math.tan((this.camera.fov * Math.PI / 180) / 2)

    let viewRadius: number
    if (name === "Sun") {
      viewRadius = ownOrbitWorld("Mercury")
    } else if (name === "Earth") {
      // Frame the Earth-Moon system
      viewRadius = kmToWorld(ORBIT_RADII_KM.Moon) * 3
    } else if (name === "Moon") {
      viewRadius = kmToWorld(ORBIT_RADII_KM.Moon)
    } else {
      // Use 2% of the body's orbital radius as the neighborhood
      const orbit = ownOrbitWorld(name)
      viewRadius = orbit > 0 ? orbit * 0.02 : 0.1
    }

    // Distance so viewRadius fills 1/3 of viewport width
    return 3 * viewRadius / (halfFovTan * aspect)
  }

  // --- Spacecraft orbit management ---

  /** Rebuild the spacecraft's current orbit line (called on init and after maneuvers) */
  private rebuildSpacecraftOrbit() {
    // Remove old
    if (this.spacecraftOrbitGroup) {
      this.scene.remove(this.spacecraftOrbitGroup)
      if (this.spacecraftOrbitEntry) {
        const idx = this.orbitGlows.indexOf(this.spacecraftOrbitEntry)
        if (idx >= 0) this.orbitGlows.splice(idx, 1)
      }
    }

    if (!this.engine.state.currentOrbit) return

    try {
      const positions = this.registry.computeAllPositions(this.engine.time.currentTimeJD)
      const soiPos = positions.get(this.engine.state.currentSOIBody) || Vector3d.Zero
      const soiThree = vec3dToThree(soiPos)

      // Generate points in body-relative coordinates (group position tracks SOI body)
      const orbitPoints = generateOrbitPath(this.engine.state.currentOrbit, 128)
      const linePoints = orbitPoints.map((p) => vec3dToThree(p))

      const { group, entry } = makeGlowLine(linePoints, SPACECRAFT_COLOR, 3, 0.04)
      group.position.copy(soiThree)
      this.scene.add(group)
      this.spacecraftOrbitGroup = group
      this.spacecraftOrbitEntry = entry
      this.orbitGlows.push(entry)
    } catch (e) {
      console.warn("Failed to rebuild spacecraft orbit:", e)
    }
  }

  // --- Burn Planner ---

  private formatDv(ms: number): string {
    const abs = Math.abs(ms)
    if (abs >= 1_000_000) return (ms / 1000).toFixed(0) + " km/s"
    if (abs >= 10_000) return (ms / 1000).toFixed(1) + " km/s"
    return ms.toFixed(0) + " m/s"
  }

  private initBurnPanel() {
    const panel = document.createElement("div")
    panel.className = "sim-burn-panel"
    panel.setAttribute("data-simulation-target", "burnPanel")
    const maxDv = Math.ceil(this.engine.state.spacecraft.availableDeltaV)
    // Choose step so slider has ~1000 discrete positions for usable precision
    const step = Math.max(1, Math.pow(10, Math.floor(Math.log10(maxDv)) - 2))
    const dvUnit = maxDv >= 10_000 ? "km/s" : "m/s"
    const dvBudget = maxDv >= 10_000 ? (maxDv / 1000).toFixed(0) : String(maxDv)
    panel.innerHTML = `
      <div class="sim-burn-title">BURN PLANNER</div>
      <div class="sim-burn-row">
        <label>Prograde</label>
        <input type="range" min="-${maxDv}" max="${maxDv}" value="0" step="${step}" data-axis="prograde" data-action="input->simulation#updateBurnParam">
        <span class="sim-burn-value" data-burn-display="prograde">0 m/s</span>
      </div>
      <div class="sim-burn-row">
        <label>Normal</label>
        <input type="range" min="-${maxDv}" max="${maxDv}" value="0" step="${step}" data-axis="normal" data-action="input->simulation#updateBurnParam">
        <span class="sim-burn-value" data-burn-display="normal">0 m/s</span>
      </div>
      <div class="sim-burn-row">
        <label>Radial</label>
        <input type="range" min="-${maxDv}" max="${maxDv}" value="0" step="${step}" data-axis="radial" data-action="input->simulation#updateBurnParam">
        <span class="sim-burn-value" data-burn-display="radial">0 m/s</span>
      </div>
      <div class="sim-burn-info">
        <span>Total &Delta;V: <strong data-burn-display="totalDv">0</strong> / ${dvBudget} ${dvUnit}</span>
        <span class="sim-burn-position">Burn at: <strong data-burn-display="position">T+0s</strong></span>
      </div>
      <div class="sim-burn-hint">A / D — scroll burn time (Shift = 30 days)</div>
      <div class="sim-burn-actions">
        <button class="sim-burn-btn sim-burn-btn--commit" data-action="simulation#commitBurn">EXECUTE</button>
        <button class="sim-burn-btn sim-burn-btn--reset" data-action="simulation#resetBurn">RESET</button>
      </div>`
    this.canvasTarget.parentElement!.appendChild(panel)

    // Vertical burn-position slider along left edge
    const burnAtSlider = document.createElement("div")
    burnAtSlider.className = "sim-burn-at"
    burnAtSlider.innerHTML = `
      <input type="range" min="0" max="1000" value="0" step="1"
             class="sim-burn-at-slider"
             orient="vertical"
             data-action="input->simulation#updateBurnAt">`
    this.canvasTarget.parentElement!.appendChild(burnAtSlider)

    // Keyboard listener for burn point scrubbing
    document.addEventListener("keydown", (e) => this.onBurnKeydown(e))

    // Create burn marker: a cone (thrust indicator) pointing along prograde
    // ConeGeometry points along +Y by default; we'll orient it in updateBurnMarker
    const markerGeo = new THREE.ConeGeometry(0.35, 1, 8)
    // Shift geometry so the base sits at origin (tip points +Y)
    markerGeo.translate(0, 0.5, 0)
    const markerMat = new THREE.MeshBasicMaterial({
      color: PREVIEW_COLOR,
      transparent: true,
      opacity: 0.9,
    })
    this.burnMarkerMesh = new THREE.Mesh(markerGeo, markerMat)
    this.burnMarkerMesh.visible = false
    this.scene.add(this.burnMarkerMesh)

    // Arrival marker — diamond shape, shows where spacecraft will be at periapsis
    const arrGeo = new THREE.OctahedronGeometry(0.5, 0)
    const arrMat = new THREE.MeshBasicMaterial({
      color: 0xff6644, // warm red-orange, distinct from cyan burn marker
      transparent: true,
      opacity: 0.9,
    })
    this.arrivalMarkerMesh = new THREE.Mesh(arrGeo, arrMat)
    this.arrivalMarkerMesh.visible = false
    this.scene.add(this.arrivalMarkerMesh)
  }

  /** Compute the state vector at the burn point (current orbit propagated by time offset) */
  private computeBurnPointState(): StateVector {
    const gm = this.registry.getGM(this.engine.state.currentSOIBody)
    const orbit = this.engine.state.currentOrbit
    return elementsToState(orbit, gm, this.computeBurnTimeJD())
  }

  /** Time (JD) at which the burn would fire */
  private computeBurnTimeJD(): number {
    return this.engine.time.currentTimeJD + this.burnTimeOffset / SECONDS_PER_DAY
  }

  /**
   * Compute prograde/normal/radial basis vectors at the burn point.
   */
  private computeBurnFrame(): { prograde: Vector3d, normal: Vector3d, radial: Vector3d } {
    const state = this.computeBurnPointState()
    const vel = state.velocity
    const pos = state.position

    const prograde = vel.normalized
    const h = Vector3d.cross(pos, vel)
    const normal = h.normalized
    const radial = Vector3d.cross(prograde, normal).normalized

    return { prograde, normal, radial }
  }

  private computeBurnDeltaV(): Vector3d {
    const { prograde, normal, radial } = this.computeBurnFrame()
    const pKms = this.burnPrograde / 1000
    const nKms = this.burnNormal / 1000
    const rKms = this.burnRadial / 1000
    return prograde.scale(pKms).add(normal.scale(nKms)).add(radial.scale(rKms))
  }

  private updateBurnPreview() {
    // Remove old preview
    if (this.previewGroup) {
      this.scene.remove(this.previewGroup)
      if (this.previewGlowEntry) {
        const idx = this.orbitGlows.indexOf(this.previewGlowEntry)
        if (idx >= 0) this.orbitGlows.splice(idx, 1)
      }
      this.previewGroup = null
      this.previewGlowEntry = null
    }

    // Update burn marker position
    this.updateBurnMarker()

    // Update position display
    this.updateBurnPositionDisplay()

    const totalDv = Math.sqrt(this.burnPrograde ** 2 + this.burnNormal ** 2 + this.burnRadial ** 2)
    if (totalDv < 0.1) {
      if (this.arrivalMarkerMesh) this.arrivalMarkerMesh.visible = false
      this.updateOrbitInfoDisplay()
      return
    }

    const burnState = this.computeBurnPointState()
    const deltaV = this.computeBurnDeltaV()
    const gm = this.registry.getGM(this.engine.state.currentSOIBody)

    const postBurnState: StateVector = {
      position: burnState.position,
      velocity: burnState.velocity.add(deltaV),
      time: burnState.time,
      centralBody: burnState.centralBody,
    }

    try {
      const postBurnElements = stateToElements(postBurnState, gm)
      const pathPoints = generateOrbitPath(postBurnElements, 128)

      const positions = this.registry.computeAllPositions(this.engine.time.currentTimeJD)
      const soiPos = positions.get(this.engine.state.currentSOIBody) || Vector3d.Zero
      const soiThree = vec3dToThree(soiPos)

      // Body-relative coordinates — group position tracks SOI body
      const linePoints = pathPoints.map((p) => vec3dToThree(p))

      const { group, entry } = makeGlowLine(linePoints, PREVIEW_COLOR, 2, 0.025, 0.8)
      group.position.copy(soiThree)
      this.scene.add(group)
      this.previewGroup = group
      this.previewGlowEntry = entry
      this.orbitGlows.push(entry)

      // Arrival marker: propagate post-burn orbit to periapsis (half period)
      this.updateArrivalMarker(postBurnElements, postBurnState, gm)

      // Update orbital info with preview
      this.updateOrbitInfoDisplay(postBurnElements)
    } catch (e) {
      console.warn("Burn preview failed:", e)
    }
  }

  private updateArrivalMarker(postBurnElements: OrbitalElements, postBurnState: StateVector, gm: number) {
    if (!this.arrivalMarkerMesh) return

    // Only meaningful for elliptical transfer orbits
    if (postBurnElements.eccentricity >= 1.0 || postBurnElements.semiMajorAxis <= 0) {
      this.arrivalMarkerMesh.visible = false
      return
    }

    // Propagate to half the orbital period (arrival at the opposite apse)
    const T = TWO_PI * Math.sqrt(
      Math.abs(postBurnElements.semiMajorAxis) ** 3 / gm
    )
    const halfPeriodDays = (T / 2) / SECONDS_PER_DAY
    const arrivalTimeJD = postBurnState.time + halfPeriodDays

    const arrivalState = elementsToState(postBurnElements, gm, arrivalTimeJD)

    // Position in world coordinates at the burn display time
    const displayTimeJD = this.computeBurnTimeJD()
    const positions = this.registry.computeAllPositions(displayTimeJD)
    const soiPos = positions.get(this.engine.state.currentSOIBody) || Vector3d.Zero
    const worldPos = vec3dToThree(arrivalState.position.add(soiPos))

    this.arrivalMarkerMesh.position.copy(worldPos)
    this.arrivalMarkerMesh.visible = true

    // Scale like spacecraft marker
    const camDist = this.camera.position.distanceTo(this.controls.target)
    this.arrivalMarkerMesh.scale.setScalar(Math.max(camDist * 0.05, 0.0001))
  }

  private updateBurnMarker() {
    if (!this.burnMarkerMesh) return

    const hasBurn = Math.abs(this.burnPrograde) > 0.1 ||
                    Math.abs(this.burnNormal) > 0.1 ||
                    Math.abs(this.burnRadial) > 0.1 ||
                    this.burnTimeOffset > 60

    if (!hasBurn) {
      this.burnMarkerMesh.visible = false
      return
    }

    const burnState = this.computeBurnPointState()
    const positions = this.registry.computeAllPositions(this.engine.time.currentTimeJD)
    const soiPos = positions.get(this.engine.state.currentSOIBody) || Vector3d.Zero
    const worldPos = vec3dToThree(burnState.position.add(soiPos))

    this.burnMarkerMesh.position.copy(worldPos)
    this.burnMarkerMesh.visible = true

    // Orient cone along prograde direction (velocity at burn point)
    const prograde = burnState.velocity.normalized
    const progradeWorld = vec3dToThree(prograde).normalize()
    // lookAt trick: place a target point along prograde from current position
    const target = worldPos.clone().add(progradeWorld)
    this.burnMarkerMesh.lookAt(target)
    // ConeGeometry points +Y, but lookAt points +Z, so rotate to align
    this.burnMarkerMesh.rotateX(Math.PI / 2)

    // Scale: small relative to camera distance
    const camDist = this.camera.position.distanceTo(this.controls.target)
    this.burnMarkerMesh.scale.setScalar(Math.max(camDist * 0.04, 0.0001))
  }

  private updateBurnPositionDisplay() {
    const panel = this.canvasTarget.parentElement
    if (!panel) return
    const display = panel.querySelector('[data-burn-display="position"]')
    if (!display) return

    const s = this.burnTimeOffset
    const days = s / 86400
    if (days < 1) {
      const hours = s / 3600
      display.textContent = `T+${hours.toFixed(1)}h`
    } else if (days < 30) {
      display.textContent = `T+${days.toFixed(1)}d`
    } else if (days < 365) {
      display.textContent = `T+${(days / 30.44).toFixed(1)}mo`
    } else {
      display.textContent = `T+${(days / 365.25).toFixed(2)}y`
    }
  }

  private onBurnKeydown(event: KeyboardEvent) {
    // Don't capture keys when typing in inputs
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return

    // Time step: 1 day normally, 30 days with shift
    const step = event.shiftKey ? 30 * 86400 : 86400

    if (event.key === "d" || event.key === "D") {
      this.burnTimeOffset = Math.min(this.burnTimeOffset + step, SimulationController.MAX_BURN_TIME_OFFSET)
      this.syncBurnAtSlider()
      this.updateBurnPreview()
      event.preventDefault()
    } else if (event.key === "a" || event.key === "A") {
      this.burnTimeOffset = Math.max(this.burnTimeOffset - step, 0)
      this.syncBurnAtSlider()
      this.updateBurnPreview()
      event.preventDefault()
    }
  }

  /** Sync the vertical burn-at slider to match current burnTimeOffset */
  private syncBurnAtSlider() {
    const slider = this.canvasTarget.parentElement?.querySelector<HTMLInputElement>('.sim-burn-at-slider')
    if (slider) {
      // Inverse of cubic: slider = cbrt(timeOffset / maxOffset) * 1000
      const t = this.burnTimeOffset / SimulationController.MAX_BURN_TIME_OFFSET
      slider.value = String(Math.round(Math.cbrt(t) * 1000))
    }
  }

  // --- Stimulus Actions ---

  updateBurnAt(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const sliderVal = parseFloat(input.value)
    // Cubic mapping: 0→0, 1000→5 years. Fine control near zero.
    const t = sliderVal / 1000
    const timeSeconds = (t * t * t) * SimulationController.MAX_BURN_TIME_OFFSET

    const hasBurnDv = Math.abs(this.burnPrograde) > 0.1 ||
                      Math.abs(this.burnNormal) > 0.1 ||
                      Math.abs(this.burnRadial) > 0.1

    if (hasBurnDv) {
      // Planning a burn: slider controls burn timing
      this.burnTimeOffset = timeSeconds
      this.updateBurnPreview()
    } else {
      // No burn planned: slider scrubs sim time forward
      const baseTimeJD = this.engine.state.startTimeJD
      const newTimeJD = baseTimeJD + timeSeconds / SECONDS_PER_DAY
      this.engine.time.setTime(newTimeJD)

      // Re-propagate spacecraft to new time
      const gm = this.registry.getGM(this.engine.state.currentSOIBody)
      this.engine.state.spacecraft.state = elementsToState(
        this.engine.state.currentOrbit, gm, newTimeJD
      )
      this.engine.events.fireSpacecraftMoved(this.engine.state.spacecraft.state)
      this.engine.events.fireTimeChanged(newTimeJD)
    }
  }

  updateBurnParam(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const axis = input.dataset.axis
    const val = parseFloat(input.value)

    if (axis === "prograde") this.burnPrograde = val
    else if (axis === "normal") this.burnNormal = val
    else if (axis === "radial") this.burnRadial = val

    // Update display values
    const panel = this.canvasTarget.parentElement!
    const display = panel.querySelector(`[data-burn-display="${axis}"]`)
    if (display) display.textContent = this.formatDv(val)

    const totalDv = Math.sqrt(this.burnPrograde ** 2 + this.burnNormal ** 2 + this.burnRadial ** 2)
    const totalDisplay = panel.querySelector('[data-burn-display="totalDv"]')
    if (totalDisplay) totalDisplay.textContent = this.formatDv(totalDv)

    this.updateBurnPreview()
  }

  commitBurn() {
    const totalDv = Math.sqrt(this.burnPrograde ** 2 + this.burnNormal ** 2 + this.burnRadial ** 2)
    if (totalDv < 0.1) return

    const deltaV = this.computeBurnDeltaV()
    const gm = this.registry.getGM(this.engine.state.currentSOIBody)

    // If burn is at current position (no offset), apply immediately and
    // update visuals synchronously — don't rely on the async tick→event chain.
    // For future burns (offset > 0), queue for engine execution.
    if (this.burnTimeOffset < 60) { // less than 1 minute = immediate
      // Apply burn directly to spacecraft state
      this.engine.state.spacecraft.applyImpulsiveBurn(deltaV)
      this.engine.state.currentOrbit = stateToElements(this.engine.state.spacecraft.state, gm)
      this.engine.events.fireOrbitChanged(this.engine.state.currentOrbit)
      this.engine.events.fireFuelFractionChanged(this.engine.state.spacecraft.fuelFraction)
      this.engine.events.fireDeltaVChanged(this.engine.state.spacecraft.availableDeltaV)
    } else {
      // Propagate spacecraft to the burn point, then apply burn
      const burnTimeJD = this.computeBurnTimeJD()
      const burnState = this.computeBurnPointState()

      // Move spacecraft to burn point state
      this.engine.state.spacecraft.state = burnState
      // Apply the burn
      this.engine.state.spacecraft.applyImpulsiveBurn(deltaV)
      // Recompute orbit from post-burn state
      this.engine.state.currentOrbit = stateToElements(this.engine.state.spacecraft.state, gm)
      // Advance sim time to burn point
      this.engine.time.setTime(burnTimeJD)

      this.engine.events.fireOrbitChanged(this.engine.state.currentOrbit)
      this.engine.events.fireFuelFractionChanged(this.engine.state.spacecraft.fuelFraction)
      this.engine.events.fireDeltaVChanged(this.engine.state.spacecraft.availableDeltaV)
    }

    // Check if burn moved spacecraft outside current SOI
    this.engine.forceSOICheck()

    // Evaluate conditions after burn (since burns are synchronous, not via tick)
    this.evaluateConditionsAfterBurn()

    this.resetBurn()
  }

  /** Check victory/failure conditions after a synchronous burn */
  private evaluateConditionsAfterBurn() {
    if (!this.conditionEvaluator) return

    const results = this.conditionEvaluator.evaluate(
      this.engine.state,
      this.registry,
      this.engine.time.currentTimeJD,
    )

    for (const { conditionId, isMet, isVictory } of results) {
      if (!isMet) continue
      if (isVictory) {
        this.engine.events.fireVictoryConditionMet(conditionId)
      } else {
        this.engine.events.fireFailureConditionMet(conditionId)
      }
    }
  }

  resetBurn() {
    this.burnPrograde = 0
    this.burnNormal = 0
    this.burnRadial = 0
    this.burnTimeOffset = 0
    this.syncBurnAtSlider()

    // Reset sliders and displays
    if (this.hasBurnPanelTarget) {
      const panel = this.burnPanelTarget
      for (const input of panel.querySelectorAll<HTMLInputElement>("input[type=range]")) {
        input.value = "0"
      }
      for (const display of panel.querySelectorAll<HTMLElement>(".sim-burn-value")) {
        display.textContent = "0"
      }
      const totalDisplay = panel.querySelector('[data-burn-display="totalDv"]')
      if (totalDisplay) totalDisplay.textContent = "0"
    }

    // Remove preview
    if (this.previewGroup) {
      this.scene.remove(this.previewGroup)
      if (this.previewGlowEntry) {
        const idx = this.orbitGlows.indexOf(this.previewGlowEntry)
        if (idx >= 0) this.orbitGlows.splice(idx, 1)
      }
      this.previewGroup = null
      this.previewGlowEntry = null
    }

    // Hide markers
    if (this.burnMarkerMesh) this.burnMarkerMesh.visible = false
    if (this.arrivalMarkerMesh) this.arrivalMarkerMesh.visible = false
  }

  pause() {
    this.engine.pause()
  }

  setSpeed(event: Event) {
    const btn = event.currentTarget as HTMLElement
    const speed = parseInt(btn.dataset.speed || "1", 10)
    this.engine.time.isPaused = false
    this.engine.setTimeAcceleration(speed)
  }
}
